'use client';

import { useMemo } from 'react';
import { encodeQr } from '@/lib/qr/encode';

/**
 * Tekent een QR-code als SVG, zonder externe afhankelijkheid.
 *
 * Eén pad met alle donkere modules erin in plaats van een rechthoek per module:
 * dat scheelt bij een code van versie 5 al ruim duizend elementen, en de
 * browser hoeft er maar één te tekenen.
 *
 * De stille rand van vier modules zit in de viewBox. Zonder die rand kan een
 * scanner de code niet van zijn omgeving losmaken - dat is de klassieke reden
 * dat een QR die er prima uitziet toch niet werkt.
 */
export function QrCode({
  text,
  className,
  title,
}: {
  text: string;
  className?: string;
  title?: string;
}) {
  const { path, extent } = useMemo(() => {
    const matrix = encodeQr(text);
    const quiet = 4;
    const parts: string[] = [];
    for (let row = 0; row < matrix.size; row += 1) {
      let run = 0;
      for (let col = 0; col <= matrix.size; col += 1) {
        const dark = col < matrix.size && matrix.modules[row][col];
        if (dark) {
          run += 1;
          continue;
        }
        if (run > 0) {
          parts.push(`M${quiet + col - run} ${quiet + row}h${run}v1h-${run}z`);
          run = 0;
        }
      }
    }
    return { path: parts.join(''), extent: matrix.size + quiet * 2 };
  }, [text]);

  return (
    <svg
      viewBox={`0 0 ${extent} ${extent}`}
      className={className}
      role="img"
      aria-label={title || 'QR-code'}
      shapeRendering="crispEdges"
    >
      <rect width={extent} height={extent} fill="#ffffff" />
      <path d={path} fill="#0f172a" />
    </svg>
  );
}
